/**
 * DemoLeads — owner-only outreach tracker. Lists every prospect we've emailed
 * an interactive demo to (borrower via "Send demo link" / "Copy demo link", and
 * lender via "Send lender demo"), pulled from the backend demo_requests log.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Send, Building2, Users, Search, Loader2, RefreshCw, Home } from 'lucide-react';

const NAVY = '#0b3b6f';
const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
};

export default function DemoLeads({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | borrower | lender

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/demo/leads`, { params: { user_id: user?.id } });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load demo leads.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.id) load(); }, [user?.id]);

  const rows = useMemo(() => {
    const list = data?.leads || [];
    const term = q.trim().toLowerCase();
    return list
      .filter((l) => (filter === 'all' ? true : l.type === filter))
      .filter((l) => (term
        ? [l.email, l.name, l.company].filter(Boolean).some((v) => v.toLowerCase().includes(term))
        : true));
  }, [data, q, filter]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8" data-testid="demo-leads-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ letterSpacing: '-0.5px' }}>
              Demo leads
            </h1>
            <p className="text-sm text-slate-500 mt-1">Prospects you&apos;ve sent an interactive DrawReady demo to.</p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 font-semibold text-sm px-3.5 py-2 rounded-xl hover:bg-white"
            data-testid="demo-leads-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-6">
          {[
            { label: 'Total leads', value: data?.total ?? 0, icon: Send, tone: NAVY },
            { label: 'Borrower demos', value: data?.borrower_count ?? 0, icon: Home, tone: '#5c9e33' },
            { label: 'Lender demos', value: data?.lender_count ?? 0, icon: Building2, tone: '#0369a1' },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`demo-leads-stat-${s.label}`}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${s.tone}18` }}>
                  <s.icon className="w-4 h-4" style={{ color: s.tone }} />
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{s.label}</span>
              </div>
              <div className="text-2xl font-extrabold text-slate-900 mt-2">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 mt-6 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email, name or company…"
              className="w-full border border-slate-300 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="demo-leads-search"
            />
          </div>
          <div className="flex items-center gap-1.5">
            {[['all', 'All'], ['borrower', 'Borrower'], ['lender', 'Lender']].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`text-sm font-semibold rounded-lg px-3 py-2 border transition ${filter === k ? 'text-white border-transparent' : 'text-slate-600 border-slate-300 hover:border-slate-400'}`}
                style={filter === k ? { background: NAVY } : {}}
                data-testid={`demo-leads-filter-${k}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-2xl mt-4 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400" data-testid="demo-leads-loading">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : error ? (
            <div className="py-16 text-center text-slate-500" data-testid="demo-leads-error">{error}</div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-slate-400" data-testid="demo-leads-empty">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No demo leads yet. Use “Send demo link” or “Send lender demo” to invite a prospect.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="px-4 py-3 font-bold">Contact</th>
                    <th className="px-4 py-3 font-bold">Company</th>
                    <th className="px-4 py-3 font-bold">Type</th>
                    <th className="px-4 py-3 font-bold text-center">Sends</th>
                    <th className="px-4 py-3 font-bold">Last sent</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((l) => (
                    <tr key={l.email} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`demo-lead-row-${l.email}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{l.name || '—'}</div>
                        <div className="text-slate-500 text-xs">{l.email}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{l.company || '—'}</td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full"
                          style={l.type === 'lender'
                            ? { background: '#e0f2fe', color: '#0369a1' }
                            : { background: '#eaf3e0', color: '#3f6b1f' }}
                        >
                          {l.type === 'lender' ? <Building2 className="w-3 h-3" /> : <Home className="w-3 h-3" />}
                          {l.type === 'lender' ? 'Lender' : 'Borrower'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center font-semibold text-slate-700">{l.request_count || 1}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(l.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
